import React, {useEffect, useState} from "react";
import {useNavigate, useParams, useLocation} from "react-router-dom";
import {useSelector} from "react-redux";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import VLFTextField from "@/components/Input/TextField";
import VLFDatePicker from "@/components/Input/DatePicker";
import VenueSelect from "@/components/VenueSelect";
import MatchSportingConfig, {
	defaultSportingPeriods,
	editionSportingDefaults,
	sportingPayload,
	sportingPeriodsError,
	sportingStructureLocked,
} from "@/components/MatchSportingConfig";
import VLFSelectNew from "@/components/Input/SelectNew";

import {ClubAPI} from "@/api/club";
import {CompetitionSeasonAPI} from "@/api/competitionSeason";
import {MatchAPI} from "@/api/match";
import VLFModal from "@/components/Modal";
import {Checkbox, FormControlLabel} from "@mui/material";
import {useCategories} from "@/utils/hooks";
import {RosterAPI} from "@/api/roster";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

import {
	getInternalClubId,
	REQUIRED_FIELD,
	SUCCESSFUL_OPERATION,
	ERROR_OPERATION,
} from "@/utils/constants";
import {
	checkRequiredFields,
	getRosterName,
	visibleRosters,
} from "@/utils/functions";

import "./index.scss";

const DEFAULT_MATCH_TYPE = {value: "Campionato", label: "Campionato"};
const MATCH_TYPES = ["Campionato", "Torneo", "Coppa", "Amichevole"].map(
	(value) => ({value, label: value}),
);
const DEFAULT_DURATION = 90;

const HandleMatchPage = (props) => {
	const {edit, handleNotification, scoutingContext = false} = props;

	const basePath = "/matches";

	const navigate = useNavigate();
	const {id} = useParams();
	const location = useLocation();

	const seasonId = useSelector(
		(state) => location.state?.seasonId || state?.season?.id,
	);
	const season = useSelector(
		(state) =>
			state?.seasons?.find((s) => s.id === location.state?.seasonId) ||
			state?.season,
	);
	const userId = useSelector((state) => state?.user?.userId);
	const clubId = useSelector((state) => state?.user?.clubId);
	const userName = useSelector((state) => state?.user?.userName);

	const [clubs, setClubs] = useState([]);
	const [competitionSeasons, setCompetitionSeasons] = useState([]);
	const [participants, setParticipants] = useState([]);
	const {categories} = useCategories({formatted: true, handleNotification});
	const [rounds, setRounds] = useState([]);
	const [homeRosters, setHomeRosters] = useState([]);
	const [awayRosters, setAwayRosters] = useState([]);
	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState([]);
	const [originalRosters, setOriginalRosters] = useState({});
	const [rosterConfirmation, setRosterConfirmation] = useState(null);
	const [sportingCustomized, setSportingCustomized] = useState(false);
	const [calendarCustomized, setCalendarCustomized] = useState(false);
	const prefilledDate = location.state?.startDate
		? new Date(location.state.startDate)
		: null;

	const [fields, setFields] = useState({
		scoutingPlanned: scoutingContext,
		observedMatch: scoutingContext,
		startDate: prefilledDate,
		competitionSeason: null,
		round: null,
		homeClub: null,
		homeRoster: null,
		awayClub: null,
		awayRoster: null,
		matchType: location.state?.matchType
			? {value: location.state.matchType, label: location.state.matchType}
			: DEFAULT_MATCH_TYPE,
		duration: DEFAULT_DURATION,
		sportingPeriods: edit ? null : defaultSportingPeriods(),
		allowReentry: false,
	});

	useEffect(() => {
		if (edit || sportingCustomized) return;
		const edition = fields.competitionSeason?.value
			? competitionSeasons.find(
					(cs) => cs.value === fields.competitionSeason.value,
				)
			: fields.matchType?.value === "Amichevole"
				? competitionSeasons.find(
						(cs) =>
							cs.competitionType === "Amichevole" && cs.seasonId === seasonId,
					)
				: null;
		setFields((prev) => ({
			...prev,
			...editionSportingDefaults(edition),
			...(calendarCustomized ? {duration: prev.duration} : {}),
		}));
	}, [
		edit,
		sportingCustomized,
		calendarCustomized,
		competitionSeasons,
		fields.competitionSeason?.value,
		fields.matchType?.value,
		seasonId,
	]);

	useEffect(() => {
		if (
			!edit &&
			(fields.homeClub?.value === getInternalClubId() ||
				fields.awayClub?.value === getInternalClubId())
		)
			setFields((previous) =>
				previous.observedMatch ? {...previous, observedMatch: false} : previous,
			);
	}, [edit, fields.homeClub?.value, fields.awayClub?.value]);

	// ── Data loading ──────────────────────────────────────────────────────────

	const getClubs = async () => {
		try {
			const response = await ClubAPI.list();
			setClubs(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const loadRostersForClub = async (clubIdVal, setter) => {
		try {
			const response = await RosterAPI.list({
				seasonId,
				clubId: clubIdVal,
				prospect: false,
			});
			setter(
				visibleRosters((response || []).filter((r) => !r.prospect)).map(
					(r) => ({
						value: r.id,
						label: getRosterName(r),
					}),
				),
			);
		} catch {
			setter([]);
		}
	};

	const getCompetitionSeasons = async () => {
		try {
			const response = await CompetitionSeasonAPI.list();
			setCompetitionSeasons(
				response.map((cs) => ({
					value: cs.id,
					label: (cs.competition?.name || "") + " - " + (cs.season?.name || ""),
					seasonId: cs.season?.id,
					defaultSportingPeriods: cs.defaultSportingPeriods,
					defaultAllowReentry: cs.defaultAllowReentry,
					competitionType: cs.competition?.competitionType,
				})),
			);
			if (!edit && location.state?.competitionSeasonId) {
				const cs = response.find(
					(e) => e.id === location.state.competitionSeasonId,
				);
				if (cs) {
					setFields((prev) => ({
						...prev,
						competitionSeason: {
							value: cs.id,
							label: cs.competition?.name + " — " + cs.season?.name,
						},
					}));
					await loadRounds(cs.id);
				}
			}
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const getMatch = async () => {
		setLoading(true);
		try {
			const response = await MatchAPI.getById(id, {seasonId});
			setOriginalRosters({
				home: response.homeRosterId,
				away: response.awayRosterId,
			});
			response.startDate = response.startDate
				? new Date(response.startDate)
				: null;
			response.duration = moment
				.duration(moment(response?.endDate).diff(moment(response?.startDate)))
				.asMinutes();
			response.homeClub = {
				value: response.homeClubId,
				label: response.homeClub,
			};
			response.awayClub = {
				value: response.awayClubId,
				label: response.awayClub,
			};
			response.matchType = {
				value: response.matchType,
				label: response.matchType,
			};
			response.venue = response.venueId
				? {value: response.venueId, label: response.fieldName}
				: null;
			response.category = response.playersCategoryId
				? {value: response.playersCategoryId, label: response.playersCategory}
				: null;

			// Load rosters for both clubs and pre-populate
			if (response.homeClubId) {
				loadRostersForClub(response.homeClubId, (opts) => {
					setHomeRosters(opts);
					if (response.homeRosterId) {
						response.homeRoster =
							opts.find((o) => o.value === response.homeRosterId) || null;
						setFields((prev) => ({...prev, homeRoster: response.homeRoster}));
					}
				});
			}
			if (response.awayClubId) {
				loadRostersForClub(response.awayClubId, (opts) => {
					setAwayRosters(opts);
					if (response.awayRosterId) {
						response.awayRoster =
							opts.find((o) => o.value === response.awayRosterId) || null;
						setFields((prev) => ({...prev, awayRoster: response.awayRoster}));
					}
				});
			}

			// Build competitionSeason option — fetch if not populated by the API
			if (response.competitionSeasonId) {
				let cs = response.competitionSeason;
				if (!cs?.competition) {
					try {
						cs = await CompetitionSeasonAPI.getById(
							response.competitionSeasonId,
						);
					} catch {}
				}
				response.competitionSeason = {
					value: response.competitionSeasonId,
					label: cs
						? (cs.competition?.name || "") + " - " + (cs.season?.name || "")
						: response.competitionSeasonId,
					seasonId: cs?.season?.id,
					competitionType: cs?.competition?.competitionType,
				};
				setParticipants(cs?.participants || []);
				response.homeParticipant = cs?.participants?.find(
					(p) => p.id === response.homeParticipantId,
				);
				response.awayParticipant = cs?.participants?.find(
					(p) => p.id === response.awayParticipantId,
				);
				response.editionCategoryId = cs?.playerCategoryId;
				// Build round options from the already-fetched CS
				const roundOptions = [
					...(cs?.groups || []).flatMap((g) => g.rounds || []),
				]
					.sort((a, b) => {
						const da = a.startDate ? new Date(a.startDate) : null;
						const db = b.startDate ? new Date(b.startDate) : null;
						if (da && db) return da - db;
						return (a.roundNumber ?? 0) - (b.roundNumber ?? 0);
					})
					.map((r) => ({
						value: r.id,
						label: r.name
							? `${r.roundNumber} — ${r.name}`
							: `Giornata ${r.roundNumber}`,
					}));
				setRounds(roundOptions);

				// Pre-populate round using the built options (label already resolved)
				if (response.competitionSeasonGroupRoundId) {
					response.round = roundOptions.find(
						(o) => o.value === response.competitionSeasonGroupRoundId,
					) ?? {value: response.competitionSeasonGroupRoundId, label: ""};
				}
			}

			response.observedMatch =
				!response.competitionSeasonId && Boolean(response.observedMatch);
			setFields(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getClubs();
		getCompetitionSeasons();
		if (edit) getMatch();
	}, [edit]);

	// ── Rounds of the selected competition season ─────────────────────────────

	const findParticipant = (items, club, roster) => {
		const candidates = (items || []).filter(
			(p) => p.clubId === club?.value && p.rosterId === (roster?.value || null),
		);
		return candidates.length === 1 ? candidates[0] : null;
	};
	const loadRounds = async (csId) => {
		if (!csId) {
			setRounds([]);
			setParticipants([]);
			return;
		}
		try {
			const cs = await CompetitionSeasonAPI.getById(csId);
			setParticipants(cs.participants || []);
			setFields((prev) => ({
				...prev,
				category: cs.playerCategoryId
					? {value: cs.playerCategoryId, label: cs.playerCategory}
					: prev.category,
				editionCategoryId: cs.playerCategoryId,
				matchType: cs.competition?.competitionType
					? {
							value: cs.competition.competitionType,
							label: cs.competition.competitionType,
						}
					: prev.matchType,
				homeParticipant: findParticipant(
					cs.participants,
					prev.homeClub,
					prev.homeRoster,
				),
				awayParticipant: findParticipant(
					cs.participants,
					prev.awayClub,
					prev.awayRoster,
				),
			}));
			setRounds(
				[...(cs.groups || []).flatMap((g) => g.rounds || [])]
					.sort((a, b) => {
						const da = a.startDate ? new Date(a.startDate) : null;
						const db = b.startDate ? new Date(b.startDate) : null;
						if (da && db) return da - db;
						return (a.roundNumber ?? 0) - (b.roundNumber ?? 0);
					})
					.map((r) => ({
						value: r.id,
						label: r.name
							? `${r.roundNumber} — ${r.name}`
							: `Giornata ${r.roundNumber}`,
					})),
			);
		} catch {
			setRounds([]);
		}
	};

	// ── Competition seasons filtered by current Redux season ──────────────────

	const filteredCompetitionSeasons = seasonId
		? competitionSeasons.filter((cs) => cs.seasonId === seasonId)
		: competitionSeasons;

	const participantMode = Boolean(
		fields.competitionSeason?.value && fields.matchType?.value !== "Amichevole",
	);
	const selectTeam = (side, participant) => {
		setErrors([]);
		setFields((prev) => ({
			...prev,
			[side + "Participant"]: participant,
			[side + "Club"]: participant
				? {value: participant.clubId, label: participant.clubName}
				: null,
			[side + "Roster"]: participant?.rosterId
				? {value: participant.rosterId, label: participant.name}
				: null,
		}));
	};
	const teamSelect = (side, label) => (
		<div className="col-sm-12 col-md-6">
			<VLFSelectNew
				label={label}
				required
				options={participants.map((p) => ({
					...p,
					value: p.id,
					label: p.clubName + " — " + p.name,
				}))}
				value={
					fields[side + "Participant"]
						? {
								value:
									fields[side + "Participant"].id ||
									fields[side + "Participant"].value,
								label:
									fields[side + "Participant"].clubName +
									" — " +
									fields[side + "Participant"].name,
							}
						: null
				}
				onChange={(p) => selectTeam(side, p)}
				error={errors.includes(side + "Participant")}
			/>
		</div>
	);

	// ── Validation ────────────────────────────────────────────────────────────

	const getRequiredFields = () => {
		const required = [
			"startDate",
			"duration",
			"homeClub",
			"awayClub",
			"matchType",
		];
		if (participantMode) required.push("homeParticipant", "awayParticipant");
		if (fields.homeClub?.value === clubId) required.push("homeRoster");
		if (fields.awayClub?.value === clubId) required.push("awayRoster");
		if (fields.matchType?.value !== "Amichevole" && !fields.observedMatch)
			required.push("competitionSeason");
		return required;
	};

	// ── Save ──────────────────────────────────────────────────────────────────

	const persistMatch = async (body) => {
		setLoading(true);
		try {
			const response = edit
				? await MatchAPI.update(id, body)
				: await MatchAPI.create(body);
			setRosterConfirmation(null);
			navigate(`${basePath}/${response?.id || id}`, {
				state: {
					returnTo:
						location.state?.returnTo ||
						(scoutingContext ? "/scouting/planning" : "/matches"),
					initialTab: fields.scoutingPlanned ? "scouting" : "main_info",
				},
			});
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(
				error.response?.data?.error || ERROR_OPERATION,
				"error",
			);
		} finally {
			setLoading(false);
		}
	};

	const saveMatch = async () => {
		try {
			const errs = checkRequiredFields(getRequiredFields(), fields);
			if (Number(fields.duration) <= 0) errs.push("duration");
			const sportingError = sportingPeriodsError(fields.sportingPeriods);
			if (sportingError) {
				handleNotification(sportingError, "error");
				return;
			}
			setErrors(errs);

			if (!errs.length) {
				setLoading(true);

				const body = {
					...fields,
					...sportingPayload(fields),
					seasonId,
					observedMatch: Boolean(fields.observedMatch),
					scoutingPlanned: Boolean(fields.scoutingPlanned),
					playersCategoryId:
						fields.editionCategoryId || fields.category?.value || null,
					homeParticipantId: participantMode
						? fields.homeParticipant?.id ||
							fields.homeParticipant?.value ||
							null
						: null,
					awayParticipantId: participantMode
						? fields.awayParticipant?.id ||
							fields.awayParticipant?.value ||
							null
						: null,
					endDate: moment(fields?.startDate)
						.add(fields?.duration, "minutes")
						.toISOString(),
					competitionSeasonId: fields?.competitionSeason?.value ?? null,
					competitionSeasonGroupRoundId: fields?.round?.value ?? null,
					homeClubId: fields?.homeClub.value,
					awayClubId: fields?.awayClub.value,
					homeRosterId: fields?.homeRoster?.value ?? null,
					awayRosterId: fields?.awayRoster?.value ?? null,
					away: clubs?.find((c) => fields?.awayClub.value === c.id)?.internal,
					matchConvocations: [],
					clubId,
					userId,
					user: {userId, userName},
					matchType: fields.matchType.value,
					venueId: fields.venue?.value || null,
					fieldName: fields.venue?.label || null,
				};

				delete body.homeClub;
				delete body.awayClub;
				delete body.homeRoster;
				delete body.awayRoster;
				delete body.round;

				const changed = edit
					? ["home", "away"].filter(
							(side) =>
								body[side + "RosterId"] &&
								body[side + "RosterId"] !== originalRosters[side] &&
								body[side + "ClubId"] !== clubId,
						)
					: [];
				if (changed.length) {
					const players = (await MatchAPI.getPlayers(id)) || [];
					const targets = await Promise.all(
						changed.map(async (side) => {
							const roster = await RosterAPI.getById(body[side + "RosterId"]);
							const members = new Set(
								(roster.players || []).map((p) => p.playerId || p.player?.id),
							);
							const known = [
								...new Map(
									players
										.filter(
											(p) =>
												p.matchSide === side &&
												p.clubId === body[side + "ClubId"] &&
												p.playerId &&
												!members.has(p.playerId),
										)
										.map((p) => [p.playerId, p]),
								).values(),
							];
							return {side, name: roster.name, players: known};
						}),
					);
					const additions = targets.filter((t) => t.players.length);
					if (additions.length) {
						setRosterConfirmation({body, targets: additions});
						setLoading(false);
						return;
					}
				}
				await persistMatch(body);
			}
		} catch (error) {
			setLoading(false);
			handleNotification(
				error.response?.data?.error || ERROR_OPERATION,
				"error",
			);
		}
	};

	const createClub = async (name, fieldKey) => {
		try {
			const response = await ClubAPI.create({name});
			setFields({
				...fields,
				[fieldKey]: {value: response.id, label: response.name},
			});
			getClubs();
		} catch (error) {
			handleNotification(
				error.response?.data?.error || ERROR_OPERATION,
				"error",
			);
		}
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		saveMatch();
	};

	const goBack = () => {
		if (edit)
			navigate(basePath + "/" + id, {
				state: {returnTo: location.state?.returnTo},
			});
		else navigate(-1);
	};

	// ── Actions ───────────────────────────────────────────────────────────────

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: goBack,
			color: "info",
		},
		{
			icon: <SaveIcon />,
			title: "Salva",
			onClick: handleSubmit,
			disabled: !!checkRequiredFields(getRequiredFields(), fields).length,
		},
	];

	const breadcrumbsElements = [{label: "Calendario", path: "/calendar"}];
	if (edit) {
		breadcrumbsElements.push(
			{label: "/"},
			{
				label: fields
					? (fields.homeClub?.label || "") +
						" - " +
						(fields.awayClub?.label || "")
					: "",
				path: basePath + "/" + id,
			},
		);
	}

	// ── Render ────────────────────────────────────────────────────────────────

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={edit ? "Modifica" : "Nuova partita"}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="sheet-container">
				<div className="row no-margin">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Informazioni principali"
							content={
								<div className="row">
									{scoutingContext && !edit && (
										<div className="col-12">
											<FormControlLabel
												control={
													<Checkbox
														checked={Boolean(fields.scoutingPlanned)}
														onChange={(e) =>
															setFields({
																...fields,
																scoutingPlanned: e.target.checked,
															})
														}
													/>
												}
												label="Da osservare (planning scouting)"
											/>
										</div>
									)}
									{scoutingContext && (
										<div className="col-12">
											<FormControlLabel
												control={
													<Checkbox
														checked={Boolean(fields.observedMatch)}
														onChange={(e) =>
															setFields({
																...fields,
																observedMatch: e.target.checked,
															})
														}
													/>
												}
												label="Competizione da inserire (se sconosciuta)"
											/>
										</div>
									)}
									<div className="col-sm-12 col-md-6">
										<VLFSelectNew
											label="Tipo partita"
											options={MATCH_TYPES}
											value={fields.matchType}
											onChange={(matchType) =>
												setFields({
													...fields,
													matchType,
													competitionSeason: null,
													round: null,
													editionCategoryId: null,
													homeParticipant: null,
													awayParticipant: null,
												})
											}
										/>
									</div>
									<div className="col-sm-12 col-md-6">
										<VLFSelectNew
											label="Categoria (facoltativa)"
											options={categories}
											value={fields.category || null}
											isClearable
											editable={!fields.editionCategoryId}
											onChange={(category) => setFields({...fields, category})}
										/>
									</div>
									<div className="col-sm-12 col-md-6">
										<VLFDatePicker
											type={fields.startTimeToBeConfirmed ? "date" : "datetime"}
											label="Data e ora"
											value={fields.startDate}
											onChange={(value) => {
												setErrors([]);
												setFields({...fields, startDate: value});
											}}
											required
											helperText={
												errors.indexOf("startDate") !== -1 ? REQUIRED_FIELD : ""
											}
											error={errors.indexOf("startDate") !== -1}
											size="small"
											minDate={
												season?.startDate
													? new Date(season.startDate)
													: undefined
											}
											maxDate={
												season?.endDate ? new Date(season.endDate) : undefined
											}
										/>
										<FormControlLabel
											control={
												<Checkbox
													checked={!!fields.startTimeToBeConfirmed}
													onChange={(e) =>
														setFields({
															...fields,
															startTimeToBeConfirmed: e.target.checked,
														})
													}
												/>
											}
											label="Orario da definire"
										/>
									</div>
									<div className="col-sm-12 col-md-6">
										<VLFTextField
											value={fields["duration"] ?? ""}
											onChange={(e) => {
												setErrors([]);
												setCalendarCustomized(true);
												setFields({...fields, duration: e.target.value});
											}}
											required
											helperText={
												errors.indexOf("duration") !== -1 ? REQUIRED_FIELD : ""
											}
											label="Durata in calendario (minuti)"
											type="number"
											size="small"
										/>
									</div>
									<div className="col-sm-12 col-md-6">
										<VLFSelectNew
											options={filteredCompetitionSeasons}
											value={fields.competitionSeason}
											onChange={(newValue) => {
												setErrors([]);
												setFields({
													...fields,
													competitionSeason: newValue,
													editionCategoryId: null,
													homeParticipant: null,
													awayParticipant: null,
													round: null,
													matchType: newValue?.competitionType
														? {
																value: newValue.competitionType,
																label: newValue.competitionType,
															}
														: fields.matchType,
												});
												loadRounds(newValue?.value ?? null);
											}}
											label="Competizione"
											isClearable
											required={
												fields.matchType?.value !== "Amichevole" &&
												!fields.observedMatch
											}
											editable={true}
											helperText={
												errors.indexOf("competitionSeason") !== -1
													? REQUIRED_FIELD
													: ""
											}
											error={errors.indexOf("competitionSeason") !== -1}
										/>
									</div>
									<div className="col-sm-12 col-md-6">
										<VLFSelectNew
											options={rounds}
											value={fields.round}
											onChange={(newValue) =>
												setFields({...fields, round: newValue})
											}
											label="Giornata"
											isClearable
											sorted={false}
											isDisabled={
												!fields.competitionSeason || rounds.length === 0
											}
										/>
									</div>
									{participantMode ? (
										<>
											{teamSelect("home", "Squadra casa")}
											{teamSelect("away", "Squadra ospite")}
											<div className="col-12 handle-match__hint">
												Squadra non in elenco? Va iscritta nella competizione,
												dalla pagina Competizioni.
											</div>
										</>
									) : (
										<>
											<div className="col-sm-12 col-md-6">
												<VLFSelectNew
													options={clubs.map((c) => ({
														value: c.id,
														label: c.name,
													}))}
													value={fields.homeClub}
													onChange={(newValue) => {
														setErrors([]);
														setFields({
															...fields,
															homeClub: newValue,
															homeRoster: null,
															homeParticipant: null,
														});
														if (newValue?.value)
															loadRostersForClub(
																newValue.value,
																setHomeRosters,
															);
														else setHomeRosters([]);
													}}
													label="Club casa"
													onCreateOption={(value) =>
														createClub(value, "homeClub")
													}
													required
													helperText={
														errors.indexOf("homeClub") !== -1
															? REQUIRED_FIELD
															: ""
													}
													error={errors.indexOf("homeClub") !== -1}
												/>
											</div>
											<div className="col-sm-12 col-md-6">
												<VLFSelectNew
													options={homeRosters}
													value={fields.homeRoster}
													onChange={(newValue) => {
														setFields({
															...fields,
															homeRoster: newValue,
															homeParticipant: null,
														});
													}}
													label="Rosa casa"
													required={fields.homeClub?.value === clubId}
													error={errors.includes("homeRoster")}
													helperText={
														errors.includes("homeRoster")
															? "Indica la rosa del tuo club"
															: ""
													}
													isClearable
													sorted={false}
													isDisabled={!homeRosters.length}
												/>
											</div>
											<div className="col-sm-12 col-md-6">
												<VLFSelectNew
													options={clubs.map((c) => ({
														value: c.id,
														label: c.name,
													}))}
													value={fields.awayClub}
													onChange={(newValue) => {
														setErrors([]);
														setFields({
															...fields,
															awayClub: newValue,
															awayRoster: null,
															awayParticipant: null,
														});
														if (newValue?.value)
															loadRostersForClub(
																newValue.value,
																setAwayRosters,
															);
														else setAwayRosters([]);
													}}
													label="Club ospite"
													onCreateOption={(value) =>
														createClub(value, "awayClub")
													}
													required
													helperText={
														errors.indexOf("awayClub") !== -1
															? REQUIRED_FIELD
															: ""
													}
													error={errors.indexOf("awayClub") !== -1}
												/>
											</div>
											<div className="col-sm-12 col-md-6">
												<VLFSelectNew
													options={awayRosters}
													value={fields.awayRoster}
													onChange={(newValue) => {
														setFields({
															...fields,
															awayRoster: newValue,
															awayParticipant: null,
														});
													}}
													label="Rosa ospite"
													required={fields.awayClub?.value === clubId}
													error={errors.includes("awayRoster")}
													helperText={
														errors.includes("awayRoster")
															? "Indica la rosa del tuo club"
															: ""
													}
													isClearable
													sorted={false}
													isDisabled={!awayRosters.length}
												/>
											</div>
										</>
									)}
									<div className="col-sm-12 col-md-6">
										<VLFTextField
											type="number"
											readOnly={fields.result?.automatic}
											value={fields["homeClubGoals"] ?? ""}
											onChange={(e) =>
												setFields({...fields, homeClubGoals: e.target.value})
											}
											label={
												fields.result?.source === "events"
													? "Gol casa (dalla cronaca)"
													: "Gol casa"
											}
											size="small"
											helperText={
												errors.indexOf("homeClubGoals") !== -1
													? REQUIRED_FIELD
													: ""
											}
											error={errors.indexOf("homeClubGoals") !== -1}
										/>
									</div>
									<div className="col-sm-12 col-md-6">
										<VLFTextField
											type="number"
											readOnly={fields.result?.automatic}
											value={fields["awayClubGoals"] ?? ""}
											onChange={(e) =>
												setFields({...fields, awayClubGoals: e.target.value})
											}
											label={
												fields.result?.source === "events"
													? "Gol ospite (dalla cronaca)"
													: "Gol ospite"
											}
											size="small"
											helperText={
												errors.indexOf("awayClubGoals") !== -1
													? REQUIRED_FIELD
													: ""
											}
											error={errors.indexOf("awayClubGoals") !== -1}
										/>
									</div>
									<div className="col-sm-12 col-md-6">
										<VenueSelect
											associationClubId={fields.homeClub?.value || null}
											value={fields.venue || null}
											onChange={(venue) =>
												setFields((prev) => ({...prev, venue}))
											}
											handleNotification={handleNotification}
										/>
									</div>
									<div className="col-sm-12 col-md-6">
										<VLFTextField
											value={fields["description"] || ""}
											onChange={(e) =>
												setFields({...fields, description: e.target.value})
											}
											label="Descrizione"
											size="small"
											multiline
											minRows={3}
											maxRows={3}
										/>
									</div>
									<div className="col-sm-12 col-md-6">
										<VLFTextField
											value={fields["videoUrl"] || ""}
											onChange={(e) =>
												setFields({...fields, videoUrl: e.target.value})
											}
											label="Link video (YouTube, Vimeo)"
											size="small"
										/>
									</div>
								</div>
							}
							openable={false}
						/>
					</div>
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Tempi di gioco"
							openable={false}
							content={
								<MatchSportingConfig
									periods={fields.sportingPeriods}
									allowReentry={fields.allowReentry}
									locked={sportingStructureLocked(fields)}
									onChange={(value) => {
										setSportingCustomized(true);
										setFields((prev) => ({...prev, ...value}));
									}}
								/>
							}
						/>
					</div>
				</div>
			</div>
			<VLFModal
				open={!!rosterConfirmation}
				title="Aggiungi i giocatori alla rosa?"
				close={() => !loading && setRosterConfirmation(null)}
				disabled={loading}
				confirmLabel="Aggiungi e salva"
				confirm={() =>
					persistMatch({
						...rosterConfirmation.body,
						assignExistingPlayersToRosters: rosterConfirmation.targets.map(
							(t) => t.side,
						),
					})
				}
				content={
					<div>
						<p>
							Vuoi aggiungere alla rosa scelta i giocatori già presenti in
							distinta?
						</p>
						{rosterConfirmation?.targets.map((target) => (
							<div key={target.side}>
								<strong>{target.name}</strong>
								<ul>
									{target.players.map((p) => (
										<li key={p.playerId}>
											{p.playerName ||
												p.player?.firstName + " " + p.player?.lastName}
										</li>
									))}
								</ul>
							</div>
						))}
						<p>
							Restano anche nelle altre rose. Chi non ha una scheda va prima
							abbinato a un giocatore.
						</p>
						<MainActions
							actions={[
								{
									title: "Salva senza aggiungerli",
									variant: "outlined",
									disabled: loading,
									onClick: () => persistMatch(rosterConfirmation.body),
								},
							]}
						/>
					</div>
				}
			/>
		</Page>
	);
};

export default HandleMatchPage;
